/**
 * Single source of truth for resolving a human-readable display name
 * from any "user-like" record (Firebase Auth User, Firestore users
 * doc, profile store entry, search result, etc.).
 *
 * Precedence (richest signal first):
 *   1. `firstName + lastName` (Firestore/profile fields — admin-maintained)
 *   2. `displayName` (Firebase Auth — sometimes synced from sign-up)
 *   3. `name` (legacy / generic)
 *   4. `email` (last-resort — exposed to make missing-name bugs obvious)
 *   5. `fallback` (default: "Unknown User")
 *
 * Whitespace-only values are treated as absent.
 *
 * Example:
 *   composeDisplayName(profile)
 *   composeDisplayName(user, 'Anonymous')
 *   composeDisplayName({ ...user, ...profile })   // merge sources
 */
export interface DisplayNameSource {
  firstName?: string | null;
  lastName?: string | null;
  displayName?: string | null;
  name?: string | null;
  email?: string | null;
  // Tolerate arbitrary extra fields so callers can pass full user objects.
  [k: string]: any;
}

const _clean = (v: unknown): string =>
  (typeof v === 'string' ? v.trim() : '') || '';

export function composeDisplayName(
  source: DisplayNameSource | null | undefined,
  fallback: string = 'Unknown User',
): string {
  if (!source) return fallback;
  const first = _clean(source.firstName);
  const last = _clean(source.lastName);
  const full = `${first} ${last}`.trim();
  if (full) return full;
  const dn = _clean(source.displayName);
  if (dn) return dn;
  const nm = _clean(source.name);
  if (nm) return nm;
  const em = _clean(source.email);
  if (em) return em;
  return fallback;
}

/**
 * Convenience wrapper for the Firebase-auth + Firestore-profile combo
 * used on most page-level "current user" callsites. Profile (Firestore)
 * is preferred since it carries firstName/lastName admins maintain;
 * auth user is the secondary source.
 */
export function composeAuthDisplayName(
  authUser: DisplayNameSource | null | undefined,
  profile?: DisplayNameSource | null,
  fallback: string = 'Unknown User',
): string {
  const merged: DisplayNameSource = {};
  if (profile) Object.assign(merged, profile);
  if (authUser) {
    // Don't let auth's blank displayName clobber a real profile name.
    for (const k of Object.keys(authUser)) {
      const v = (authUser as any)[k];
      if (v != null && v !== '' && (merged as any)[k] == null) {
        (merged as any)[k] = v;
      }
    }
  }
  return composeDisplayName(merged, fallback);
}
